import React from 'react';
import { observer } from 'mobx-react-lite';

import { ILinkButtonFields, IWidget, ParsedEntry } from '@/contentful/types';

import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useParkingWatchForSelectedApartment } from '@hooks/useParkingWatchForSelectedApartment';
import { apartmentStore } from '@stores/apartmentStore';
import { ParkingSpaceWatchButton } from '@views/parking/components/ParkingSpaceWatchButton';

import { linkButtonVariantMap } from '../../utils/entry.utils';

import { WidgetButtonLayout } from './widgetButton.utils';

/**
 * Button for ordering a parking space watch. Shown with the same rules as on the parking front page:
 * only when the real estate has parking products and the user has no watch for the selected apartment yet.
 *
 * Contentful options:
 * - `customTranslations.description`: text shown above the button, hidden together with the button
 * - `customTranslations.buttonText`: button text, defaults to the app translation
 * - `parameters.variant`: `"contained" | "outlined" | "text"`, same values as in Link Button
 */
export const ParkingSpaceWatchWidget = observer(
  ({ customTranslations, parameters }: ParsedEntry<IWidget>) => {
    const { t } = useCustomTranslations(customTranslations);
    const variant = parameters?.variant as ILinkButtonFields['variant'];
    const { parkingWatchesForApartment, isLoading } =
      useParkingWatchForSelectedApartment();

    const hasParkingProducts =
      !!apartmentStore.selectedApartment?.realestate.parkingAreas?.some(
        (area) => area.hasParkingProducts
      );

    if (
      isLoading ||
      !hasParkingProducts ||
      !!parkingWatchesForApartment?.length
    ) {
      return null;
    }

    return (
      <WidgetButtonLayout description={t('description')}>
        <ParkingSpaceWatchButton
          variant={linkButtonVariantMap[variant ?? 'no variant']}
          className="w-fit"
        >
          {t('buttonText') || undefined}
        </ParkingSpaceWatchButton>
      </WidgetButtonLayout>
    );
  }
);
